<template>
  <section class="create-ticket-content__scrollable">
    <article class="create-ticket-content__article">
      <TextFieldStandard
        :hideIconX="true"
        :label="t('general__data__ticket-name')"
        :prefilledText="formData?.title ?? ''"
        :required="true"
        style="margin-bottom: 16px"
        @emitInput="handleNameInput"
      />
      <TextAreaStandard
        :label="t('description')"
        :prefilledText="formData?.description ?? ''"
        style="margin-bottom: -8px"
        @emitInput="handleDescriptionInput"
      />
    </article>
    <article class="create-ticket-content__article__details">
      <DropdownStandard
        class="create-ticket-content__article__details__dropdown"
        :label="t('general__text__status')"
        :title="formData?.status ? translatedStatus(formData.status) : ''"
        :options="statusOptions"
        :required="true"
        @select="handleDropdownSelection('status', $event)"
      />
      <DropdownStandard
        class="create-ticket-content__article__details__dropdown"
        :label="t('general__text__priority')"
        :title="formData?.priority ? translatedPriority(formData.priority) : ''"
        :options="priorityOptions"
        :required="true"
        @select="handleDropdownSelection('priority', $event)"
      />
      <DropdownStandard
        class="create-ticket-content__article__details__dropdown"
        :disabled="!categoryOptions.length"
        :label="t('general__text__category')"
        :title="selectedCategoryTitle"
        :options="categoryOptions"
        :required="true"
        @select="handleDropdownSelection('categoryId', $event)"
      />
      <DropdownStandard
        class="create-ticket-content__article__details__dropdown"
        :disabled="!typeOptions.length"
        :label="t('general__text__type')"
        :title="selectedTypeTitle"
        :options="typeOptions"
        :required="true"
        @select="handleDropdownSelection('typeId', $event)"
      />
      <TextFieldStandard
        :hideIconX="true"
        :label="t('general__text__start-date')"
        :prefilledText="
          formData?.startDate ? new Date(formData.startDate).toLocaleDateString() : ''
        "
        type="date"
        class="create-ticket-content__article__details__input"
        @emitInput="handleStartDateInput"
      />
      <TextFieldStandard
        :hideIconX="true"
        :label="t('general__text__due-date')"
        :prefilledText="formData?.dueDate ? new Date(formData.dueDate).toLocaleDateString() : ''"
        type="date"
        class="create-ticket-content__article__details__input"
        @emitInput="handleDueDateInput"
      />
    </article>
    <article
      v-if="badgeText.length"
      class="create-ticket-content__article"
      style="padding-top: 16px"
    >
      <span>{{ t('general__text__linked-objects') }}:</span>
      <div class="create-ticket-content__article__place-badges">
        <BadgeSquare :badgeText="badgeText" :grey="true" size="small" />
      </div>
    </article>
  </section>
</template>
<script lang="ts" setup>
import { computed, inject, onMounted, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'
import { useSelectionStore } from '@/pinia/selection'
import { getEntityParents } from '@/utils/entityHelpers'
import {
  getPriorityOptions,
  getStatusOptions,
  translatedPriority,
  translatedStatus
} from '@/utils/ticketHelpers'
import {
  CreateTicket,
  getTicketCategories,
  getTicketTypesForCategory,
  TicketCategory,
  TicketType
} from '@/services/tickets.service'
import { optionType } from '@/design-system/molecules/DropdownStandard.vue'
import BadgeSquare from '@/design-system/atoms/BadgeSquare.vue'
import DropdownStandard from '@/design-system/molecules/DropdownStandard.vue'
import TextAreaStandard from '@/design-system/atoms/TextAreaStandard.vue'
import TextFieldStandard from '@/design-system/atoms/TextFieldStandard.vue'

const { locale, t } = useI18n()
const store = useStore()

const badgeText = ref<string>('')
const categoryOptions = ref<optionType[]>([])
const priorityOptions = ref<optionType[]>([])
const statusOptions = ref<optionType[]>([])
const typeOptions = ref<optionType[]>([])

const selectionStore = useSelectionStore()
const { clickedEntityType } = storeToRefs(selectionStore)
const selectedObjectId = computed(() => {
  return store.getters.selectedObjectId
})

const formData = inject<CreateTicket>('formDataCreateErrand')
const selectedCategoryId = computed(() => formData?.categoryId)
const selectedCategoryTitle = computed(() => {
  if (!formData || !formData?.categoryId.length)
    return t('general__text__select') + ' ' + t('general__text__category').toLowerCase()
  return categoryOptions.value.find((category) => category.id === formData.categoryId)?.name ?? ''
})
const selectedTypeTitle = computed(() => {
  if (!formData || !formData?.typeId.length)
    return t('general__text__select') + ' ' + t('general__text__type').toLowerCase()
  return typeOptions.value.find((type) => type.id === formData.typeId)?.name ?? ''
})

function addDropdownCategories(ticketCategories: TicketCategory[]) {
  const ticketCategoryOptions = ticketCategories.map((category) => {
    return {
      disabled: false,
      id: category.id,
      name: category.name[locale.value],
      selected: false
    }
  })
  categoryOptions.value = ticketCategoryOptions
}
function addDropdownTypes(typesForCategory: TicketType[]) {
  const ticketTypeOptions = typesForCategory.map((type) => {
    return {
      disabled: false,
      id: type.id,
      name: type.name[locale.value],
      selected: false
    }
  })
  typeOptions.value = ticketTypeOptions
}
function handleDueDateInput(value) {
  if (!formData) return
  formData.dueDate = value + 'T00:00:00.000Z' // Time not used for dueDate according to new design
}
function handleStartDateInput(value) {
  if (!formData) return
  formData.startDate = value + 'T00:00:00.000Z' // Time not used for startDate according to new design
}
function handleDescriptionInput(value) {
  if (!formData) return
  formData.description = value
}
function handleDropdownSelection(property: string, event) {
  if (!formData) return
  formData[property] = event
}
function handleNameInput(value) {
  if (!formData) return
  formData.title = value
}

watch(
  () => formData,
  async (newValue) => {
    if (newValue) {
      priorityOptions.value = getPriorityOptions(newValue.priority)
      statusOptions.value = getStatusOptions(newValue.status)
    }
  },
  { immediate: true }
)
watch(
  () => selectedCategoryId.value,
  async (newValue, oldValue) => {
    if (newValue?.length && newValue !== oldValue) {
      typeOptions.value = []
      if (formData) formData.typeId = ''
      const typesForCategory = await getTicketTypesForCategory(newValue)
      if (typesForCategory?.length) addDropdownTypes(typesForCategory)
    }
  }
)
onMounted(async () => {
  const ticketCategories = await getTicketCategories()
  if (ticketCategories?.length) addDropdownCategories(ticketCategories)
  const entityParents = await getEntityParents(selectedObjectId.value, clickedEntityType.value)
  badgeText.value = entityParents ?? ''
})
</script>
<style scoped lang="scss">
@use '@/design-system/design-tokens/variables.scss';
@use '@/design-system/design-tokens/font-mixins.scss';
@use '@/design-system/design-tokens/mixins.scss';

.create-ticket-content {
  &__scrollable {
    overflow-y: auto;
    overflow-x: hidden;
    @include mixins.scrollbar-vertical;
  }
  &__accordion {
    width: 100%;
  }
  &__article {
    @include font-mixins.desktop-bold-paragraph;
    color: variables.$color-greyscale-content;
    padding-bottom: 24px;
    &__details {
      display: flex;
      flex-flow: wrap;
      gap: 16px;
      margin-bottom: 16px;
      &__dropdown {
        margin-bottom: 8px;
        width: calc(50% - 8px);
        @media screen and (max-width: 1024px) {
          width: 100%;
        }
      }
      &__input {
        width: calc(50% - 8px);
        @media screen and (max-width: 1024px) {
          width: 100%;
        }
      }
    }
    &__place-badges {
      margin-top: 16px;
    }
  }
}
</style>
